/**
 * AssessmentDetailSheet Component (Phase 21.4.6)
 *
 * BottomSheet that opens when an operator taps an assessment timeline item.
 * Shows full assessment context: date, zone, SOP, task focus, full AI summary,
 * trend indicator, and manager recognition note.
 *
 * Does NOT show: video, raw scores, individual question results, rubric level scores.
 * Score visibility rule enforced — operators see trends and summaries only.
 */

import {
  Calendar,
  MapPin,
  BookOpen,
  Layers,
  TrendingUp,
  TrendingDown,
  Minus,
  Star,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';
import { BottomSheet } from './BottomSheet';
import { FeedbackWidget } from './FeedbackWidget';
import type { TimelineEvent } from '../hooks/useOperatorProgress';

// ============================================
// TYPES
// ============================================

interface AssessmentDetailSheetProps {
  /** The assessment timeline event to display (null when closed) */
  assessment: TimelineEvent | null;
  /** Controls visibility */
  isOpen: boolean;
  /** Called when the sheet should close */
  onClose: () => void;
}

// ============================================
// TREND DISPLAY
// ============================================

function TrendBadge({ trend }: { trend: TimelineEvent['trend'] }) {
  if (!trend) return null;

  switch (trend) {
    case 'improving':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
          <TrendingUp size={14} strokeWidth={1.5} />
          Improving
        </span>
      );
    case 'declining':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600">
          <TrendingDown size={14} strokeWidth={1.5} />
          Declining
        </span>
      );
    case 'steady':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-op-text-tertiary">
          <Minus size={14} strokeWidth={1.5} />
          Steady
        </span>
      );
    case 'first':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-500">
          <Sparkles size={14} strokeWidth={1.5} />
          First assessment
        </span>
      );
    default:
      return null;
  }
}

// ============================================
// DETAIL ROW
// ============================================

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-op-text-tertiary">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-op-text-tertiary">{label}</p>
        <p className="text-sm font-medium text-op-text-primary">{value}</p>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/** Format date for the sheet header: "Tuesday, Apr 8, 2026" */
function formatDetailDate(dateStr: string): string {
  const date = new Date(dateStr);
  return format(date, 'EEEE, MMM d, yyyy');
}

export function AssessmentDetailSheet({
  assessment,
  isOpen,
  onClose,
}: AssessmentDetailSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Assessment Details" snapTo="full">
      {assessment ? (
        <div className="space-y-4">
          {/* ---- Date ---- */}
          <DetailRow
            icon={<Calendar size={16} strokeWidth={1.5} />}
            label="Date"
            value={formatDetailDate(assessment.date)}
          />

          {/* ---- Zone ---- */}
          {assessment.zoneName && (
            <DetailRow
              icon={<MapPin size={16} strokeWidth={1.5} />}
              label="Zone"
              value={assessment.zoneName}
            />
          )}

          {/* ---- SOP ---- */}
          {assessment.sopTitle && (
            <DetailRow
              icon={<BookOpen size={16} strokeWidth={1.5} />}
              label="SOP"
              value={assessment.sopTitle}
            />
          )}

          {/* ---- Task Focus ---- */}
          {assessment.taskFocus && (
            <DetailRow
              icon={<Layers size={16} strokeWidth={1.5} />}
              label="Task Focus"
              value={assessment.taskFocus}
            />
          )}

          {/* ---- Trend ---- */}
          {assessment.trend && (
            <div className="pt-1">
              <TrendBadge trend={assessment.trend} />
            </div>
          )}

          {/* ---- Divider ---- */}
          <div className="border-t border-gray-100" />

          {/* ---- AI Summary ---- */}
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-op-text-tertiary">
              Analysis
            </p>
            {assessment.aiSummary ? (
              <p className="text-sm leading-relaxed text-op-text-secondary">
                {assessment.aiSummary}
              </p>
            ) : (
              <p className="text-sm italic text-op-text-tertiary">
                No analysis available
              </p>
            )}
          </div>

          {/* ---- Manager Recognition ---- */}
          {assessment.managerNote && (
            <>
              <div className="border-t border-gray-100" />
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-op-text-tertiary">
                  Manager Recognition
                </p>
                <div className="rounded-xl bg-guild-mint/20 p-3">
                  <div className="flex items-start gap-2">
                    <Star
                      size={16}
                      strokeWidth={1.5}
                      className="mt-0.5 flex-shrink-0 text-amber-500"
                      fill="currentColor"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm leading-relaxed text-op-text-primary">
                        &ldquo;{assessment.managerNote}&rdquo;
                      </p>
                      {assessment.managerName && (
                        <p className="mt-1.5 text-xs font-medium text-op-text-tertiary">
                          &mdash; {assessment.managerName}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ---- Feedback ---- */}
          <div className="border-t border-gray-100" />
          <FeedbackWidget
            targetId={assessment.id}
            feedbackType="assessment_fairness"
            questionText="Was this assessment fair?"
          />
        </div>
      ) : null}
    </BottomSheet>
  );
}
